import React from "react";
import { useImmer } from "use-immer";

import { ButtonGroup, ButtonProps, Menu, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid";

import { IgButton } from "./IgButton";
import { IgIconComponent } from "./IgIconComponent";
import { IgTooltip } from "./IgTooltip";

interface IgSplitButtonProps extends ButtonProps {
  /**
   * Hover copy for the MAIN button — why it is unavailable, usually. It never
   * reaches the caret, whose own reasons live on its menu items, and it is
   * withheld while the menu is open so it cannot sit over the menu.
   *
   * Prefer this to wrapping the whole split button in a tooltip: a wrapper
   * binds its handlers (and, under React 19, its ref) to whatever the
   * component spreads them on, and the caret must not be that.
   */
  tooltip?: React.ReactNode;
  onMenuOpenChange?: (open: boolean) => void;
  /**
   * Whether the caret is hovered or focused. Cleared when the menu closes,
   * whichever way it closed — a keyboard close leaves the caret focused, and
   * a listener that read "still active" then would never see it settle.
   */
  onMenuTriggerActiveChange?: (active: boolean) => void;
  menuOptions: {
    key: string;
    disabledMessage?: string;
    action: () => void;
    hidden?: boolean;
  }[];
}

/**
 * A primary action with the rest of its family behind a caret.
 *
 * `menuOptions[0]` is the primary: its `key` is the button label and it is
 * conventionally `hidden`, which drops it from the menu entirely rather than
 * repeating the button as a second, independently-enabled control. `disabled`
 * applies to the primary action alone — the caret is a separate control with
 * its own reasons to be unavailable, and greying it out with the primary hides
 * the alternatives exactly when the reader is looking for one (a rule with
 * nothing left to save is precisely when "Run tests" or "Unpublish" is what
 * they want). When there is nothing behind the caret, no caret is drawn.
 *
 * Every other button prop — including whatever a wrapping tooltip injects —
 * lands on the MAIN button only. The caret takes the shared `color` and
 * nothing else from the caller.
 */
export const IgSplitButton = (props: IgSplitButtonProps) => {
  const {
    menuOptions,
    startIcon,
    disabled,
    tooltip,
    onMenuOpenChange,
    onMenuTriggerActiveChange,
    ...rest
  } = props;
  const [open, setOpen] = useImmer(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);

  const [selectedIndex, setSelectedIndex] = useImmer(0);

  const closeMenu = () => {
    setOpen(false);
    onMenuOpenChange?.(false);
    onMenuTriggerActiveChange?.(false);
  };

  const handleClick = () => {
    menuOptions[0].action();
    closeMenu();
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setSelectedIndex(index);
    closeMenu();
    menuOptions[index].action();
  };

  const handleToggle = () => {
    const nextOpen = !open;
    if (nextOpen) {
      setOpen(true);
      onMenuOpenChange?.(true);
      setAnchorEl(anchorRef.current);
    } else {
      closeMenu();
    }
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    closeMenu();
    setAnchorEl(null);
  };

  const visibleOptions = menuOptions.filter((option) => !option.hidden);
  const isMenuDisabled = visibleOptions.every((option) =>
    Boolean(option.disabledMessage)
  );

  const mainButton = (
    <IgButton
      {...rest}
      disabled={disabled}
      startIcon={startIcon}
      size={"small"}
      sx={{ height: "36px" }}
      variant={"contained"}
      onClick={handleClick}
    >
      {menuOptions[0].key}
    </IgButton>
  );
  // Withheld while the menu is open: a tooltip that opened over the menu
  // would describe the button the reader has just moved on from.
  const primaryButton =
    tooltip === undefined ? (
      mainButton
    ) : (
      <IgTooltip title={open ? "" : tooltip}>{mainButton}</IgTooltip>
    );

  // Nothing behind the caret means there is no split to make — a lone primary
  // action is a plain button, and a caret onto an empty menu is a dead end.
  if (visibleOptions.length === 0) {
    return <Grid sx={{ padding: 0 }}>{primaryButton}</Grid>;
  }

  return (
    <Grid sx={{ padding: 0 }}>
      <ButtonGroup disableElevation={true} variant="contained" ref={anchorRef}>
        {primaryButton}
        <IgButton
          color={rest.color}
          disabled={isMenuDisabled}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label="More actions"
          sx={{ padding: "10px" }}
          variant={"contained"}
          size="small"
          onClick={handleToggle}
          onMouseEnter={() => onMenuTriggerActiveChange?.(true)}
          onMouseLeave={(event) =>
            onMenuTriggerActiveChange?.(
              document.activeElement === event.currentTarget
            )
          }
          onFocus={() => onMenuTriggerActiveChange?.(true)}
          onBlur={(event) =>
            onMenuTriggerActiveChange?.(event.currentTarget.matches(":hover"))
          }
        >
          <IgIconComponent color={"white"} />
        </IgButton>
      </ButtonGroup>
      <Menu
        disableScrollLock={true}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        {/* Hidden options are NOT rendered: an option drawn with
            `display: none` is invisible but still a live control — clickable
            by script, and a second Save that ignores the primary's disabled
            state. The index passed to the click handler still indexes
            `menuOptions`, so the primary keeps its conventional slot 0. */}
        {menuOptions.map((option, index) =>
          option.hidden ? null : (
            <IgTooltip key={option.key} title={option.disabledMessage}>
              <MenuItem
                disabled={!!option.disabledMessage}
                selected={index === selectedIndex}
                onClick={(event) => handleMenuItemClick(event, index)}
              >
                {option.key}
              </MenuItem>
            </IgTooltip>
          )
        )}
      </Menu>
    </Grid>
  );
};
